<template>
	<div class="mcp">
		<div class="mcp__header">
			<h2 class="mcp__title">MCP</h2>
			<p class="mcp__description">
				Click an integration to view setup instructions and copy ready-to-use config.
			</p>
		</div>

		<HeaderLoader :is-loading="loading" />

		<Message
			v-if="errorMessage"
			severity="error"
			:closable="false">
			{{ errorMessage }}
		</Message>

		<div class="mcp__section">
			<h3 class="mcp__section-title">Endpoints</h3>
			<Message
				v-if="copyErrorMessage"
				severity="error"
				:closable="false">
				{{ copyErrorMessage }}
			</Message>
			<Message
				v-if="copySuccessMessage"
				severity="success"
				:closable="false">
				{{ copySuccessMessage }}
			</Message>
			<div class="mcp__endpoint-row">
				<span class="mcp__endpoint-label">MCP endpoint</span>
				<code class="mcp__endpoint-value">{{ mcpEndpoint }}</code>
				<Button
					aria-label="Copy MCP endpoint"
					icon="fa regular fa-copy"
					severity="secondary"
					text
					@click="copyValue(mcpEndpoint, 'MCP endpoint copied')" />
			</div>
			<div class="mcp__endpoint-row">
				<span class="mcp__endpoint-label">OAuth discovery</span>
				<code class="mcp__endpoint-value">{{ oauthDiscoveryEndpoint }}</code>
				<Button
					aria-label="Copy OAuth discovery endpoint"
					icon="fa regular fa-copy"
					severity="secondary"
					text
					@click="copyValue(oauthDiscoveryEndpoint, 'OAuth discovery endpoint copied')" />
			</div>
		</div>

		<div class="mcp__section">
			<h3 class="mcp__section-title">Integration Guides</h3>
			<p class="mcp__section-note">
				Most integrations use Dynamic Client Registration (DCR). Cursor integrations use a static
				OAuth client for callback compatibility.
			</p>

			<div class="mcp__widgets-grid">
				<button
					v-for="integration in sortedIntegrations"
					:key="integration.slug"
					class="mcp__widget"
					type="button"
					:style="{ '--integration-color': getBrandColor(integration.slug) }"
					@click="openIntegration(integration)">
					<div class="mcp__widget-top">
						<div class="mcp__widget-logo-wrap">
							<img
								v-if="getLogoUrl(integration.slug) && !logoFailures.has(integration.slug)"
								class="mcp__widget-logo"
								:alt="`${integration.label} logo`"
								:src="getLogoUrl(integration.slug) || ''"
								@error="markLogoFailure(integration.slug)" />
							<span
								v-else
								class="mcp__widget-logo-fallback">
								{{ getInitials(integration.label) }}
							</span>
						</div>
						<span
							v-if="integration.one_click"
							class="mcp__widget-pill">
							One-click
						</span>
					</div>
					<span class="mcp__widget-name">{{ integration.label }}</span>
					<span class="mcp__widget-subtitle"> Open setup instructions </span>
				</button>
			</div>
		</div>

		<McpIntegrationDialog
			:integration="selectedIntegration"
			:mcp-endpoint="mcpEndpoint"
			:visible="showIntegrationDialog"
			@update:visible="showIntegrationDialog = $event" />
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import HeaderLoader from '@/components/HeaderLoader.vue'
import McpApi from '@/models/Mcp/Api'
import type { McpIntegration } from '@/models/Mcp/Types'
import McpIntegrationDialog from './McpIntegrationDialog.vue'
import { getIntegrationBranding } from './McpIntegrationAssets'

const api = new McpApi()

const loading = ref(false)
const errorMessage = ref<string | null>(null)

const copySuccessMessage = ref<string | null>(null)
const copyErrorMessage = ref<string | null>(null)
let copyMessageTimeoutId: number | null = null

const integrations = ref<McpIntegration[]>([])
const selectedIntegration = ref<McpIntegration | null>(null)
const showIntegrationDialog = ref(false)
const logoFailures = reactive(new Set<string>())

const backendUrl = new URL(import.meta.env.VITE_BACKEND_URL, window.location.origin)
const oauthHost = `${backendUrl.protocol}//${backendUrl.host}${backendUrl.pathname}`
const wellKnownHost = `${backendUrl.protocol}//${backendUrl.host}`

const mcpEndpoint = `${oauthHost}/ai/mcp`
const oauthDiscoveryEndpoint = `${wellKnownHost}/.well-known/oauth-authorization-server/ai/mcp`

const sortedIntegrations = computed(() => {
	return [...integrations.value].sort((a, b) => a.label.localeCompare(b.label))
})

onMounted(async () => {
	await fetchIntegrations()
})

async function fetchIntegrations() {
	loading.value = true
	errorMessage.value = null

	try {
		const response = await api.listIntegrations()
		integrations.value = response.data.data

		if (selectedIntegration.value) {
			selectedIntegration.value =
				integrations.value.find(
					(integration) => integration.slug === selectedIntegration.value?.slug,
				) || null
		}
	} catch (e: any) {
		errorMessage.value = e.response?.data?.message || 'Failed to load integrations'
	} finally {
		loading.value = false
	}
}

function openIntegration(integration: McpIntegration) {
	selectedIntegration.value = integration
	showIntegrationDialog.value = true
}

function getLogoUrl(slug: string): string | null {
	return getIntegrationBranding(slug)?.logo ?? null
}

function getBrandColor(slug: string): string {
	return getIntegrationBranding(slug)?.brandColor ?? '#64748B'
}

function markLogoFailure(slug: string) {
	logoFailures.add(slug)
}

function getInitials(label: string): string {
	return label
		.split(' ')
		.map((word) => word[0])
		.join('')
		.slice(0, 2)
		.toUpperCase()
}

async function copyValue(value: string, successMessage: string) {
	if (!value) return

	try {
		await navigator.clipboard.writeText(value)
		copyErrorMessage.value = null
		copySuccessMessage.value = successMessage

		if (copyMessageTimeoutId) {
			window.clearTimeout(copyMessageTimeoutId)
		}
		copyMessageTimeoutId = window.setTimeout(() => {
			copySuccessMessage.value = null
			copyMessageTimeoutId = null
		}, 2500)
	} catch {
		copySuccessMessage.value = null
		copyErrorMessage.value = 'Failed to copy to clipboard'
	}
}
</script>

<style scoped lang="scss">
.mcp {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;

	.mcp__header {
		display: flex;
		flex-direction: column;
		gap: 8px;

		.mcp__title {
			font-size: 24px;
			font-weight: 800;
			margin: 0;
		}

		.mcp__description {
			color: var(--p-text-muted-color);
			margin: 0;
			line-height: 1.5;
		}
	}

	.mcp__section {
		display: flex;
		flex-direction: column;
		gap: 12px;
		padding: 16px;
		border: 1px solid var(--p-content-border-color);
		border-radius: 10px;
		background: var(--p-surface-950);

		.mcp__section-title {
			font-size: 18px;
			font-weight: 600;
			margin: 0;
		}

		.mcp__section-note {
			margin: 0;
			font-size: 13px;
			color: var(--p-text-muted-color);
			line-height: 1.5;
		}
	}

	.mcp__endpoint-row {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 10px;
		border-radius: 8px;
		background: var(--p-surface-900);
		flex-wrap: wrap;

		.mcp__endpoint-label {
			font-size: 12px;
			color: var(--p-text-muted-color);
			min-width: 110px;
		}

		.mcp__endpoint-value {
			flex: 1;
			word-break: break-all;
			font-size: 13px;
		}
	}

	.mcp__widgets-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
		gap: 12px;
	}

	.mcp__widget {
		--integration-color: #64748b;
		border: 1px solid var(--p-content-border-color);
		background: var(--p-surface-900);
		border-radius: 10px;
		padding: 12px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
		cursor: pointer;
		text-align: left;
		transition:
			border-color 0.15s ease,
			transform 0.15s ease,
			background-color 0.15s ease;

		&:hover {
			border-color: color-mix(in srgb, var(--integration-color) 45%, var(--p-content-border-color));
			background: color-mix(in srgb, var(--p-surface-900) 88%, var(--integration-color));
			transform: translateY(-1px);
		}
	}

	.mcp__widget-top {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.mcp__widget-logo-wrap {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		background: color-mix(in srgb, var(--p-surface-800) 84%, var(--integration-color));
		display: grid;
		place-items: center;
		overflow: hidden;
		border: 1px solid
			color-mix(in srgb, var(--integration-color) 40%, var(--p-content-border-color));
		flex: 0 0 36px;
	}

	.mcp__widget-logo {
		width: 24px;
		height: 24px;
		object-fit: contain;
	}

	.mcp__widget-logo-fallback {
		font-size: 10px;
		font-weight: 700;
		color: var(--p-text-muted-color);
	}

	.mcp__widget-name {
		font-size: 13px;
		font-weight: 600;
		color: var(--p-text-color);
		line-height: 1.2;
		flex: 1;
	}

	.mcp__widget-subtitle {
		font-size: 11px;
		color: var(--p-text-muted-color);
		line-height: 1.3;
	}

	.mcp__widget-pill {
		font-size: 10px;
		line-height: 1;
		padding: 4px 6px;
		border-radius: 999px;
		background: color-mix(in srgb, var(--integration-color) 22%, transparent);
		color: var(--integration-color);
		font-weight: 700;
	}
}
</style>
